import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

import { ControlBox } from '../atoms/control-box';
import { ValueBox } from '../molecules/value-box';
import { Group } from './group';
import { useDispatch, useSelector } from '../store/utils';
import { variablesChange } from '../store/actions';

export const EnergyMetersSettings = () => {
  const [metersConfig, metersData, azRouterConfig] = useSelector(
    ({ variables }) =>
      [
        variables.EM_METERS_CONFIG,
        variables.EM_METERS_DATA,
        variables.AZ_ROUTER_CONFIG,
      ] as const
  );
  const dispatch = useDispatch();

  const [newMeterName, setNewMeterName] = useState('');
  const [editingShellyIds, setEditingShellyIds] = useState<
    Record<string, string>
  >({});

  const meterIds = Object.keys(metersConfig || {}).filter(
    (id) => metersConfig[id] != null
  );

  const azRouterIds = Object.keys(azRouterConfig || {});

  const handleAdd = () => {
    if (!newMeterName.trim()) return;
    const meterId = Math.random().toString(36).substring(2, 12);
    dispatch(
      variablesChange({
        EM_METERS_CONFIG: {
          ...metersConfig,
          [meterId]: {
            name: newMeterName.trim(),
            source: 'shelly',
            shellyId: '',
          },
        },
      })
    );
    setNewMeterName('');
  };

  const handleRemove = (meterId: string) => {
    const remainingConfig = { ...metersConfig };
    delete remainingConfig[meterId];
    const remainingData = { ...metersData };
    delete remainingData[meterId];
    dispatch(
      variablesChange({
        EM_METERS_CONFIG: remainingConfig,
        EM_METERS_DATA: remainingData,
      })
    );
  };

  const handleSourceChange = (
    meterId: string,
    source: 'shelly' | 'plc' | 'az-router'
  ) => {
    const config = metersConfig[meterId];
    if (!config) return;
    const updated = { ...config, source };
    if (source === 'plc' && !updated.plcMetric) updated.plcMetric = 'grid';
    if (source !== 'plc') delete updated.plcMetric;
    if (source === 'az-router' && !updated.azRouterId)
      updated.azRouterId = azRouterIds[0] || '';
    if (source !== 'az-router') delete updated.azRouterId;
    dispatch(
      variablesChange({
        EM_METERS_CONFIG: { ...metersConfig, [meterId]: updated },
      })
    );
  };

  const handleShellyIdChange = (meterId: string, shellyId: string) => {
    setEditingShellyIds((prev) => ({ ...prev, [meterId]: shellyId }));
  };

  const handleShellyIdBlur = (meterId: string) => {
    const newValue = editingShellyIds[meterId];
    const config = metersConfig?.[meterId];
    if (!config) return;
    if (newValue !== undefined && newValue !== config.shellyId) {
      dispatch(
        variablesChange({
          EM_METERS_CONFIG: {
            ...metersConfig,
            [meterId]: { ...config, shellyId: newValue },
          },
        })
      );
    }
    setEditingShellyIds((prev) => {
      const next = { ...prev };
      delete next[meterId];
      return next;
    });
  };

  const handleShellyIdKeyPress = (meterId: string, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
  };

  const handlePlcMetricChange = (
    meterId: string,
    plcMetric: 'grid' | 'kgj' | 'pv'
  ) => {
    const config = metersConfig[meterId];
    if (!config) return;
    dispatch(
      variablesChange({
        EM_METERS_CONFIG: {
          ...metersConfig,
          [meterId]: { ...config, plcMetric },
        },
      })
    );
  };

  const handleAzRouterIdChange = (meterId: string, azRouterId: string) => {
    const config = metersConfig[meterId];
    if (!config) return;
    dispatch(
      variablesChange({
        EM_METERS_CONFIG: {
          ...metersConfig,
          [meterId]: { ...config, azRouterId },
        },
      })
    );
  };

  const getShellyIdValue = (meterId: string) => {
    if (editingShellyIds[meterId] !== undefined)
      return editingShellyIds[meterId];
    return metersConfig?.[meterId]?.shellyId || '';
  };

  const getSourceValue = (meterId: string): 'shelly' | 'plc' | 'az-router' =>
    metersConfig[meterId]?.source || 'shelly';

  const getPlcMetricValue = (meterId: string): 'grid' | 'kgj' | 'pv' =>
    metersConfig[meterId]?.plcMetric || 'grid';

  const getAzRouterIdValue = (meterId: string): string =>
    metersConfig[meterId]?.azRouterId || '';

  return (
    <Group
      title={
        <FormattedMessage
          id="emsService.energyMeters"
          defaultMessage="Elektroměry (Shelly EM / PLC)"
        />
      }
      layout="list"
    >
      {meterIds.length === 0 && (
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="emsService.energyMeters.empty"
              defaultMessage="Nejsou nakonfigurovány žádné elektroměry"
            />
          }
          value=""
        />
      )}

      {meterIds.map((meterId) => {
        const config = metersConfig?.[meterId];
        if (!config) return null;
        const source = getSourceValue(meterId);
        return (
          <ControlBox
            key={meterId}
            title={config.name || ''}
            fullWidth
            control={
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl size="small" sx={{ width: 150 }}>
                  <InputLabel>
                    <FormattedMessage
                      id="emsService.energyMeters.source"
                      defaultMessage="Zdroj"
                    />
                  </InputLabel>
                  <Select
                    value={source}
                    label={
                      <FormattedMessage
                        id="emsService.energyMeters.source"
                        defaultMessage="Zdroj"
                      />
                    }
                    onChange={(e) =>
                      handleSourceChange(
                        meterId,
                        e.target.value as 'shelly' | 'plc' | 'az-router'
                      )
                    }
                  >
                    <MenuItem value="shelly">
                      <FormattedMessage
                        id="emsService.energyMeters.source.shelly"
                        defaultMessage="Shelly"
                      />
                    </MenuItem>
                    <MenuItem value="plc">
                      <FormattedMessage
                        id="emsService.energyMeters.source.plc"
                        defaultMessage="PLC"
                      />
                    </MenuItem>
                    <MenuItem value="az-router">
                      <FormattedMessage
                        id="emsService.energyMeters.source.azRouter"
                        defaultMessage="AZ router"
                      />
                    </MenuItem>
                  </Select>
                </FormControl>

                {source === 'shelly' && (
                  <TextField
                    label={
                      <FormattedMessage
                        id="emsService.energyMeters.shellyId"
                        defaultMessage="Shelly ID"
                      />
                    }
                    size="small"
                    value={getShellyIdValue(meterId)}
                    onChange={(e) =>
                      handleShellyIdChange(meterId, e.target.value)
                    }
                    onBlur={() => handleShellyIdBlur(meterId)}
                    onKeyPress={(e) => handleShellyIdKeyPress(meterId, e)}
                    sx={{ width: 250 }}
                  />
                )}

                {source === 'plc' && (
                  <FormControl size="small" sx={{ width: 250 }}>
                    <InputLabel>
                      <FormattedMessage
                        id="emsService.energyMeters.plcMetric"
                        defaultMessage="PLC Metrika"
                      />
                    </InputLabel>
                    <Select
                      value={getPlcMetricValue(meterId)}
                      label={
                        <FormattedMessage
                          id="emsService.energyMeters.plcMetric"
                          defaultMessage="PLC Metrika"
                        />
                      }
                      onChange={(e) =>
                        handlePlcMetricChange(
                          meterId,
                          e.target.value as 'grid' | 'kgj' | 'pv'
                        )
                      }
                    >
                      <MenuItem value="grid">
                        <FormattedMessage
                          id="emsService.energyMeters.plcMetric.grid"
                          defaultMessage="Grid"
                        />
                      </MenuItem>
                      <MenuItem value="kgj">
                        <FormattedMessage
                          id="emsService.energyMeters.plcMetric.kgj"
                          defaultMessage="KGJ"
                        />
                      </MenuItem>
                      <MenuItem value="pv">
                        <FormattedMessage
                          id="emsService.energyMeters.plcMetric.pv"
                          defaultMessage="FVE"
                        />
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}

                {source === 'az-router' && (
                  <FormControl size="small" sx={{ width: 250 }}>
                    <InputLabel>
                      <FormattedMessage
                        id="emsService.energyMeters.azRouter"
                        defaultMessage="AZ router"
                      />
                    </InputLabel>
                    <Select
                      value={getAzRouterIdValue(meterId)}
                      label={
                        <FormattedMessage
                          id="emsService.energyMeters.azRouter"
                          defaultMessage="AZ router"
                        />
                      }
                      onChange={(e) =>
                        handleAzRouterIdChange(
                          meterId,
                          e.target.value as string
                        )
                      }
                    >
                      {azRouterIds.length === 0 && (
                        <MenuItem value="" disabled>
                          <FormattedMessage
                            id="emsService.energyMeters.azRouter.empty"
                            defaultMessage="Žádné AZ routery"
                          />
                        </MenuItem>
                      )}
                      {azRouterIds.map((id) => (
                        <MenuItem key={id} value={id}>
                          {azRouterConfig?.[id]?.name || id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <IconButton
                  color="error"
                  onClick={() => handleRemove(meterId)}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Stack>
            }
          />
        );
      })}

      <ControlBox
        title={
          <FormattedMessage
            id="emsService.energyMeters.add"
            defaultMessage="Přidat nový elektroměr"
          />
        }
        fullWidth
        control={
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label={
                <FormattedMessage
                  id="emsService.energyMeters.newMeter"
                  defaultMessage="Název"
                />
              }
              size="small"
              value={newMeterName}
              onChange={(e) => setNewMeterName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
              sx={{ width: 250 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAdd}
              disabled={!newMeterName.trim()}
              size="small"
            >
              <FormattedMessage
                id="emsService.energyMeters.addButton"
                defaultMessage="Přidat"
              />
            </Button>
          </Stack>
        }
      />
    </Group>
  );
};
